/* eslint-disable jsx-a11y/label-has-associated-control */
import { useMemo } from "react";
import { useContextSelector } from "use-context-selector";
import { Field } from "@rjsf/core";

import fileUtils from "../../../utils/fileUtils";
import FileContext from "../../proposals/contexts/fileContext";
import FileShare from "../../FileShare/FileShare";
import Select from "./LoftAddOptionSelect";
import { LabelWithOptionalFlag } from "./LabelWithOptionalFlag";
import { Section, FullWidth } from "./Shared.style";
import { FileShareStyles, UploadLabel } from "./LoftFilesSelectField.styles";

const DEFAULT_OPTION_LABEL = "Select a file...";

export const LoftFilesSelect: Field = ({
  onChange,
  required,
  disabled,
  name,
  formData = {},
  uiSchema,
  schema,
  errorSchema,
}) => {
  const files = useContextSelector(FileContext, ctx => ctx.data.files);
  const filesLoading = useContextSelector(FileContext, ctx => ctx.data.loading);
  const shareFileEntity = useContextSelector(
    FileContext,
    ctx => ctx?.data?.defaultShareEntity
  );
  const parsedOptions = [
    { label: DEFAULT_OPTION_LABEL, value: "" },
    ...files.map(({ id, data }) => ({
      value: id,
      label: data?.originalFilename || "",
    })),
  ];

  const fileId = formData?.id as string;

  const isInvalid = !!(
    Object.keys(errorSchema).length > 0 && uiSchema["ui:touched"]
  );

  const allowedFileExtensions =
    (uiSchema?.["ui:options"]?.accept as string) || undefined;

  const inputFileId = useMemo(() => `fileShare_${new Date().getTime()}`, []);

  const hasOptions = parsedOptions.some(({ value }) => !!value);

  return (
    <div css={[Section, FullWidth]}>
      <LabelWithOptionalFlag
        required={required}
        tooltip={(uiSchema?.["ui:options"]?.tooltip || "") as string}
      >
        {schema.title}
      </LabelWithOptionalFlag>
      <Select
        disabled={disabled}
        description={schema.description}
        value={fileId}
        name={name}
        invalid={isInvalid}
        loading={filesLoading}
        onChange={value => {
          const selectedFile = files.find(({ id }) => id === value);
          if (selectedFile) {
            const { id, data } = selectedFile;
            const fileExtension = fileUtils.getFileExtension(selectedFile);
            const { originalFilename } = data || {};
            onChange({ id, originalFilename, fileExtension });
          } else {
            onChange({});
          }
        }}
        addNewOptionLabel={
          <>
            Don&apos;t see the CAD file you are looking for?
            <label css={UploadLabel} htmlFor={inputFileId}>
              {" Upload a CAD file."}
            </label>
          </>
        }
        options={parsedOptions}
      />
      <FileShare
        disabled={false}
        inputFileId={inputFileId}
        visible={!hasOptions}
        fileShareEntity={shareFileEntity}
        containerCss={FileShareStyles}
        shouldWrapOnPanel={false}
        onFinish={files => {
          const newUploadedFile = files?.[0];
          if (newUploadedFile) {
            const { id, data } = newUploadedFile;
            const { originalFilename } = data || {};
            const fileExtension = fileUtils.getFileExtension(
              originalFilename || ""
            );
            onChange({ id, originalFilename, fileExtension });
          }
        }}
        accept={allowedFileExtensions}
      >
        <span css={UploadLabel}>+ Upload CAD File</span>
      </FileShare>
    </div>
  );
};

export default LoftFilesSelect;
